export class PriorityQueue<T> {
    enqueue(element: T, priority: number): void {
        if (this.indexMap.has(element)) {
            this.updatePriority(element, priority)
            return
        }

        const node = {element, priority, sequence: this.sequence++}
        this.heap.push(node)
        this.indexMap.set(element, this.heap.length - 1)
        this.heapifyUp(this.heap.length - 1)

        this.verifyHeap()
    }

    dequeue(): T | null {
        if (this.heap.length === 0) {
            return null
        }

        const root = this.heap[0]
        const last = this.heap.pop()!
        this.indexMap.delete(root.element)

        if (this.heap.length > 0) {
            this.heap[0] = last
            this.indexMap.set(last.element, 0)
            this.heapifyDown(0)
        }

        this.verifyHeap()

        return root.element
    }

    // remove(element: T) {
    //     const index = this.indexMap.get(element)
    //     if (index === undefined) {
    //         throw new Error("Element not found in the queue")
    //     }
    //
    //     const last = this.heap.pop()!
    //     this.indexMap.delete(element)
    //
    //     if (index < this.heap.length) {
    //         this.heap[index] = last
    //         this.indexMap.set(last.element, index)
    //         if (this.comparator(this.heap[index].priority, last.priority)) {
    //             this.heapifyUp(index)
    //         } else {
    //             this.heapifyDown(index)
    //         }
    //     }
    //
    //     this.verifyHeap()
    // }

    // returns priority of removed item
    remove(element: T): number {
        const index = this.indexMap.get(element)
        if (index === undefined) {
            throw new Error("Element not found in the queue")
        }

        const removedNode = this.heap[index]
        const last = this.heap.pop()!
        this.indexMap.delete(element)

        if (index < this.heap.length) {
            this.heap[index] = last
            this.indexMap.set(last.element, index)
            // Compare with parent and children to decide direction
            const parentIdx = this.parent(index)
            if (index > 0 && this.comparator(this.heap[index], this.heap[parentIdx])) {
                this.heapifyUp(index)
            } else {
                this.heapifyDown(index)
            }
        }

        this.verifyHeap()
        return removedNode.priority
    }

    peek(): T | null {
        return this.heap.length > 0 ? this.heap[0].element : null
    }

    peekPriority(): number | null {
        return this.heap.length > 0 ? this.heap[0].priority : null
    }

    updatePriority(element: T, newPriority: number): void {
        const index = this.indexMap.get(element)

        if (index === undefined) {
            throw new Error("Element not found in the queue")
        }

        const {priority: oldPriority, sequence} = this.heap[index]
        if (oldPriority === newPriority) {
            return // No change
        }

        this.heap[index].priority = newPriority

        if (this.comparator({priority: newPriority, sequence}, {priority: oldPriority, sequence})) {
            this.heapifyUp(index)
        } else {
            this.heapifyDown(index)
        }

        this.verifyHeap()
    }

    // Move element to the back of its priority group (LRU behavior)
    // This is O(log n) worst case, much faster than remove + enqueue
    moveToBack(element: T): void {
        const index = this.indexMap.get(element)

        if (index === undefined) {
            throw new Error("Element not found in the queue")
        }

        // Update sequence to current (newest)
        this.heap[index].sequence = this.sequence++

        // Only heapify down since we made the element "worse" (newer sequence = lower priority within same priority level)
        // It can only move down in the heap, never up
        this.heapifyDown(index)

        this.verifyHeap()
    }

    get size(): number {
        return this.heap.length
    }

    contains(element: T): boolean {
        return this.indexMap.has(element)
    }

    isEmpty(): boolean {
        return this.heap.length === 0
    }

    clear(): void {
        this.heap.length = 0
        this.indexMap.clear()
    }

    private parent(index: number): number {
        return Math.floor((index - 1) / 2)
    }

    private leftChild(index: number): number {
        return 2 * index + 1
    }

    private rightChild(index: number): number {
        return 2 * index + 2
    }

    private swap(index1: number, index2: number): void {
        const temp = this.heap[index1]
        this.heap[index1] = this.heap[index2]
        this.heap[index2] = temp

        // Update the index map
        this.indexMap.set(this.heap[index1].element, index1)
        this.indexMap.set(this.heap[index2].element, index2)
    }

    private heapifyUp(index: number): void {
        let currentIndex = index
        while (currentIndex > 0 && this.comparator(this.heap[currentIndex], this.heap[this.parent(currentIndex)])) {
            this.swap(currentIndex, this.parent(currentIndex))
            currentIndex = this.parent(currentIndex)
        }
    }

    private heapifyDown(index: number): void {
        let currentIndex = index
        for (;;) {
            const left = this.leftChild(currentIndex)
            const right = this.rightChild(currentIndex)
            let best = currentIndex
            if (left < this.heap.length && this.comparator(this.heap[left], this.heap[best])) {
                best = left
            }
            if (right < this.heap.length && this.comparator(this.heap[right], this.heap[best])) {
                best = right
            }
            if (best === currentIndex) {
                break
            }
            this.swap(currentIndex, best)
            currentIndex = best
        }
    }

    // DIAGNOSTIC: can be enabled to catch heap/indexMap corruption at the exact operation that causes it, rather than as a downstream symptom.
    private verifyHeap() {
        if (!this.enableHeapConsistencyCheck) {
            return
        }
        if (this.heap.length !== this.indexMap.size) {
            throw new Error(`PriorityQueue: [DIAGNOSTIC] heap/indexMap size mismatch: heap.length=${this.heap.length} indexMap.size=${this.indexMap.size}`)
        }
        for (let i = 0; i < this.heap.length; i++) {
            if (this.indexMap.get(this.heap[i].element) !== i) {
                throw new Error(`PriorityQueue: [DIAGNOSTIC] indexMap out of sync at index ${i} (points to ${this.indexMap.get(this.heap[i].element)})`)
            }
            const left = this.leftChild(i)
            const right = this.rightChild(i)
            if (left < this.heap.length && this.comparator(this.heap[left], this.heap[i])) {
                throw new Error(`PriorityQueue: [DIAGNOSTIC] heap property violated at index ${i} (left child ${left})`)
            }
            if (right < this.heap.length && this.comparator(this.heap[right], this.heap[i])) {
                throw new Error(`PriorityQueue: [DIAGNOSTIC] heap property violated at index ${i} (right child ${right})`)
            }
        }
    }

    private heap: HeapNode<T>[] = []
    private indexMap: Map<T, number> = new Map()
    private comparator: (a: HeapNodeAux, b: HeapNodeAux) => boolean = (a, b) => (a.priority === b.priority ? a.sequence < b.sequence : a.priority > b.priority) // max heap by default
    private sequence = 0
    private enableHeapConsistencyCheck = false // for debugging; disabled for performance reasons
}

type HeapNodeAux = {
    priority: number
    sequence: number
}

type HeapNode<T> = HeapNodeAux & {
    element: T
}
